/**
 * This module contains utilities and type definitions for style system/style
 * panel related logic specific to supporting Style Variable Modes.
 *
 * As a rule of thumb, if you're writing new, shared code that reads/writes
 * Style Variable Modes data to/from Style Block data, or renders Style Variable
 * Modes data (encoded in StyleBlock data) to CSS, it probably belongs in here.
 */

import _ from 'lodash';
import {
  cssToPropertyMap,
  toStyleLess,
} from '@packages/systems/core/utils/CssStringUtils';
import {
  BASE_VARIABLE_MODE_ID,
  VariableCollection,
  VariableCollectionId,
  VariableModeId,
} from '@packages/systems/core/utils/StyleVariables';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import {toClassName} from '@packages/systems/style-legacy/selectors';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';
import {getOrderedBreakpointIds} from '@packages/systems/style/media-queries';
import type {BreakpointID} from '@packages/systems/style/types';
import {getPluginName} from '@packages/systems/wfdl-Qualified';
import {reportBugOnce} from '@packages/utilities/reporter';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';

const MODE_PROPERTY_PREFIX = '---mode--' as const;
const NO_USERLAND_PREFIX = {userlandPrefix: null};

/**
 * The property name for a variable mode as it appears within a StyleBlock's
 * `styleLess` data.
 */
export type VariableModeStyleBlockStylePropertyName =
  `${typeof MODE_PROPERTY_PREFIX}${VariableCollectionId | typeof BASE_VARIABLE_MODE_ID}`;

/**
 * The application of a mode as it appears within a StyleBlock's data.
 */
export type VariableModeStyleApplication =
  `${VariableModeStyleBlockStylePropertyName}: ${VariableModeId};`;

/**
 * Return `true` if a provided style property name is formatted to encode a
 * Variable Collection ID.
 */
export const isVariableModeStyleBlockStylePropertyName = (
  propertyName: string
): propertyName is VariableModeStyleBlockStylePropertyName =>
  propertyName.startsWith(MODE_PROPERTY_PREFIX);

/**
 * Given a style property name, return the associated collection ID, if
 * applicable.
 */
export const parseCollectionIdFromPropertyName = (
  propertyName: string
): VariableCollectionId | null => {
  if (isVariableModeStyleBlockStylePropertyName(propertyName)) {
    return propertyName.substring(MODE_PROPERTY_PREFIX.length);
  }
  return null;
};

/**
 * Return a CSS property name that encodes a collection ID into a custom
 * property.
 */
export const stringifyCollectionIdToPropertyName = (
  collectionId: VariableCollectionId
): VariableModeStyleBlockStylePropertyName => {
  return `${MODE_PROPERTY_PREFIX}${collectionId}`;
};

export const stringifyModeApplication = (
  collectionId: VariableCollectionId,
  modeId: VariableModeId,
  priority: 'important' | '' = ''
): VariableModeStyleApplication =>
  `${stringifyCollectionIdToPropertyName(collectionId)}: ${modeId}${priority === 'important' ? ' !important' : ''};`;

const MODE_DATA_ATTR_PREFIX = 'data-w-mode--' as const;

export const isModeDataAttributeSelector = (selector: string): boolean => {
  return selector.startsWith(`[${MODE_DATA_ATTR_PREFIX}`);
};

export const renderModeDataAttributeName = (
  collection: VariableCollection,
  slugMap: Record<string, string>
): string => {
  let libraryPrefix = '';
  const {origin} = collection;
  if (origin) {
    const slug = slugMap[getPluginName(origin)];
    if (slug) {
      libraryPrefix = `${toClassName(slug, NO_USERLAND_PREFIX)}--`;
    }
  }
  return `${MODE_DATA_ATTR_PREFIX}${libraryPrefix}${cssifyVariableCollectionName(collection.name)}`;
};

export const renderModeDataAttributeValue = (modeName: string): string => {
  return cssifyVariableModeName(modeName);
};

/**
 * Given a CSS string, a collection, and a mode, return `true` if the string
 * contains a mode-application matching the given collection-mode pair.
 */
export const containsModeApplication = (
  styleLess: string,
  collectionId: VariableCollectionId,
  modeId: VariableModeId
): boolean =>
  styleLess.includes(stringifyModeApplication(collectionId, modeId));

/**
 * Given a `styleLess` string, return one containing only its mode-application
 * properties (`---mode--<collectionId>: <modeId>;`), dropping everything else.
 * Empty when the string applied no mode.
 *
 * Works on a `styleLess` string rather than a style block so the two block
 * formats — the Designer's Immutable record and the server's raw object — can
 * share one definition of what a mode application is.
 */
export const keepOnlyModeApplications = (styleLess: string): string => {
  const properties = cssToPropertyMap(styleLess);
  const modeApplications: typeof properties = {};
  for (const [property, value] of Object.entries(properties)) {
    if (isVariableModeStyleBlockStylePropertyName(property)) {
      modeApplications[property] = value;
    }
  }
  return toStyleLess(modeApplications);
};

const ORDERED_BREAKPOINTS = getOrderedBreakpointIds();
/**
 * Given a style block (presumed to be the site's `default-body` tag style
 * block), determine which breakpoint this mode is applied on, i.e. which
 * breakpoint this mode is an "auto mode" on.
 *
 * This function assumes that there is only a single breakpoint that the mode is
 * applied on, however it's possible to set a mode on multiple breakpoints. In
 * that case, this function will return the first breakpoint encountered from
 * the result of `getOrderedBreakpointIds()`.
 *
 * TODO: It's technically possible to have multiple breakpoints apply a mode by
 * manually applying the mode on the body tag selector; how do we handle this in
 * the UX layer?
 *
 * Personally I think disallowing multiple breakpoints was a mistake, even if it
 * would be an uncommon use-case. We probably should just allow multi-select
 * instead.
 */
export const getAutoModeBreakpointId = (
  bodyTagStyleBlock: StyleBlock,
  collectionId: VariableCollectionId,
  modeId: VariableModeId
): null | BreakpointId => {
  for (const breakpointId of ORDERED_BREAKPOINTS) {
    const styleLess: string | undefined =
      breakpointId === BREAKPOINT_ID_MAIN
        ? bodyTagStyleBlock.styleLess
        : bodyTagStyleBlock.variants.get(breakpointId)?.get('styleLess');
    if (
      styleLess != null &&
      containsModeApplication(styleLess, collectionId, modeId)
    ) {
      return breakpointId;
    }
  }
  return null;
};

/**
 * Given a human-readable Variable Collection name, normalize it to something
 * compatible with CSS output.
 *
 * This serves two purposes:
 *
 * - Producing a CSS-safe string (e.g. for use in a CSS variable or class name)
 * - Safely guaranteeing CSS uniqueness when validating a collection name
 */
export const cssifyVariableCollectionName = (name: string): string => {
  return toClassName(name, NO_USERLAND_PREFIX);
};

/**
 * Given a human-readable Variable Mode name, normalize it to something
 * compatible with CSS output.
 *
 * This serves two purposes:
 *
 * - Producing a CSS-safe string (e.g. for use in a CSS variable or class name)
 * - Safely guaranteeing CSS uniqueness when validating a collection name
 */
export const cssifyVariableModeName = (name: string): string => {
  return toClassName(name, NO_USERLAND_PREFIX);
};

const _replaceModeApplicationIds =
  (collectionIdMap: Record<VariableCollectionId, VariableCollectionId>) =>
  (styleLess: string | null): string | null => {
    if (styleLess == null) return null;
    // Break our string out into individual properties:
    const properties = cssToPropertyMap(styleLess);

    // Start replacing the properties that are mode-applications that correspond
    // to a collection matching one from `collectionIdMap`:
    const updatedProperties: typeof properties = {...properties};
    for (const [property, value] of Object.entries(properties)) {
      const collectionId = parseCollectionIdFromPropertyName(property);
      if (collectionId == null) {
        continue;
      }
      const siteLevelCollectionId = collectionIdMap[collectionId];
      if (siteLevelCollectionId == null) {
        continue;
      }
      // Replace the old `collectionId` with the new one from `collectionIdMap`:
      const newProperty = stringifyCollectionIdToPropertyName(
        siteLevelCollectionId
      );

      // We need to remove the old property since its key is being replaced:
      // eslint-disable-next-line webflow/no-delete
      delete updatedProperties[property];

      // We keep the same mode ID value:
      updatedProperties[newProperty] = value;
    }
    return toStyleLess(updatedProperties);
  };

export const replaceCollectionIdsInModeApplicationsForBlock = (
  block: StyleBlock,
  collectionIdMap: Record<VariableCollectionId, VariableCollectionId>
) => {
  return block.withMutations((block_mut) => {
    block_mut.update('styleLess', _replaceModeApplicationIds(collectionIdMap));
    for (const [variantKey] of block_mut.variants) {
      block_mut.updateIn(
        ['variants', variantKey, 'styleLess'],
        _replaceModeApplicationIds(collectionIdMap)
      );
    }
  });
};

/**
 * An "auto mode" is really just the application of a variable mode that lives
 * somewhere on the `default-body` style block for the site.
 *
 * This helper function applies a mode as an "auto mode" on a given breakpoint,
 * ensuring that only one breakpoint applies the mode.
 *
 * TODO: It's technically possible to have multiple breakpoints apply a mode by
 * manually applying the mode on the body tag selector; how do we handle this in
 * the UX layer?
 *
 * Personally I think disallowing multiple breakpoints was a mistake, even if it
 * would be an uncommon use-case. We probably should just allow multi-select
 * instead.
 */
export function setAutoMode({
  state,
  collectionId,
  modeId,
  breakpointId,
}: {
  state: StyleBlockStateType;
  collectionId: VariableCollectionId;
  modeId: VariableModeId;
  breakpointId: null | BreakpointID;
}): StyleBlockStateType {
  const bodyTagStyleBlock = state.styleBlocks.get('default-body');

  // If no `default-body` style block is found, something has gone very wrong with style data,
  // so we simply abort and track the error:
  if (!bodyTagStyleBlock) {
    reportBugOnce(
      'NoDefaultBodyTagBlockFound',
      'No default-body tag style block found while attempting to set an auto-mode',
      {
        team: 'styles',
        ticket: 'https://webflow.atlassian.net/browse/STYL-1104',
      }
    );
    return state;
  }

  const currentBreakpointId = getAutoModeBreakpointId(
    bodyTagStyleBlock,
    collectionId,
    modeId
  );

  // If our auto-mode is already set as we want, return the state, unchanged:
  if (currentBreakpointId === breakpointId) return state;

  const mixinKey = stringifyCollectionIdToPropertyName(collectionId);

  // Update `styleBlockIds` to include `default-body` - without this we
  // will not export the modified `default-body` StyleBlock when saving
  // the site:
  state = state.update('styleBlockIds', (styleBlockIds) =>
    styleBlockIds.has('default-body')
      ? styleBlockIds
      : styleBlockIds.add('default-body')
  );

  // We also must clear the `fake` flag in order for the style block to be
  // persisted:
  state = state.setIn(['styleBlocks', 'default-body', 'fake'], false);

  // Remove the auto-mode from `currentBreakpointId`:
  if (currentBreakpointId != null) {
    const styleLess: string | undefined =
      currentBreakpointId === BREAKPOINT_ID_MAIN
        ? bodyTagStyleBlock.styleLess
        : bodyTagStyleBlock.variants.get(currentBreakpointId)?.get('styleLess');
    const map = cssToPropertyMap(styleLess ?? '');
    const nextStyleLess = toStyleLess(_.omit(map, [mixinKey]));
    state = state.setIn(
      currentBreakpointId === BREAKPOINT_ID_MAIN
        ? ['styleBlocks', 'default-body', 'styleLess']
        : [
            'styleBlocks',
            'default-body',
            'variants',
            currentBreakpointId,
            'styleLess',
          ],
      nextStyleLess
    );
  }

  // Add the auto-mode to the new `breakpointId`
  if (breakpointId != null) {
    const styleLess: string | undefined =
      breakpointId === BREAKPOINT_ID_MAIN
        ? bodyTagStyleBlock.styleLess
        : bodyTagStyleBlock.variants.get(breakpointId)?.get('styleLess');
    const map = cssToPropertyMap(styleLess ?? '');
    const nextStyleLess = toStyleLess({...map, [mixinKey]: modeId});
    state = state.setIn(
      breakpointId === BREAKPOINT_ID_MAIN
        ? ['styleBlocks', 'default-body', 'styleLess']
        : [
            'styleBlocks',
            'default-body',
            'variants',
            breakpointId,
            'styleLess',
          ],
      nextStyleLess
    );
  }

  return state;
}
